// Türetilmiş (ƒx) hücrede "Raporlanan değer" bloğu — KpiCellEditor.tsx'ten ayrıldı (güvenlik/UX
// taraması 2026-10-08, 600 satır sınırı; davranış birebir). Kanıt sekmesinin üstünde render edilir.

import { useState } from "react";
import type { KpiResolvedCell, KpiResolvedIndicator } from "@mahir/shared";
import { ApiError } from "../../api/client";
import { putKpiReportedValue } from "../../api/kpi";
import { patchIndicatorUnit, unitChangedCurrent } from "../../lib/kpi-unit-changed";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import { normalizeCellInput } from "../../lib/kpi-grid-nav";
import { formatKpiCell, KPI_COPY } from "../../lib/kpi-ui";
import { useKpiStore } from "../../stores/kpi-store";
import { cellInputStyle } from "./kpi-form-styles";

/**
 * D20 — raporlanan değer yazımı göstergenin (ekranda görülen) birimini beyan eder; birim bu arada
 * değiştiyse sunucu yazmaz (409 KPI_UNIT_CHANGED) ve ileti yeni birimi söyler.
 */
export async function saveReportedValue(
  indicator: KpiResolvedIndicator,
  entityId: string,
  year: number,
  numericValue: number | null,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    await putKpiReportedValue({
      metricId: indicator.metricId,
      entityId,
      year,
      numericValue,
      unit: indicator.unit ?? null,
    });
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError && e.code === "KPI_UNIT_CHANGED") {
      const to = unitChangedCurrent(e.details);
      // hücre yazımıyla aynı: danışman grid'inde birim yerelde güncellenir → sonraki giriş yeni birimle
      if (to !== undefined) {
        useKpiStore.setState((st) =>
          st.grid ? { grid: patchIndicatorUnit(st.grid, indicator.metricId, to) } : {},
        );
        // I2 — komşu hücreler yeni birimin çevrilmiş sayılarıyla gelsin (yalnız değerler)
        await useKpiStore.getState().refreshGrid();
      }
      return { ok: false, error: KPI_UNIT_COPY.writeUnitChanged(to ?? KPI_UNIT_COPY.unitless) };
    }
    return { ok: false, error: e instanceof Error ? e.message : KPI_COPY.reported.failed };
  }
}

/**
 * TODO-KPI-23 — türetilmiş (ƒx) hücrede "Raporlanan değer" girişi (danışman): sayı → `POST
 * /kpi/values/reported`; "Değer bekleniyor (XXX)" → numericValue null → hücre/Excel/rapor XXX (HESAPLANAMADI
 * değil). Mevcut "Sabitle" akışı (ReportedPopover) aynen durur. Küçük, ayrı blok — Kanıt sekmesinin üstünde.
 */
export function ReportedValueBlock(p: {
  indicator: KpiResolvedIndicator;
  cell: KpiResolvedCell | undefined;
  entityId: string;
  year: number;
  onSaved: () => void;
}): JSX.Element {
  const R = KPI_COPY.reported;
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const percent = p.indicator.valueType === "percent";
  const current = (): string | null => {
    const c = p.cell;
    if (!c?.isReported) return null;
    if (c.reportedPending) return R.pendingLabel;
    const v =
      typeof c.reportedMismatch === "number" && typeof c.value === "number"
        ? c.value - c.reportedMismatch
        : c.value;
    return typeof v === "number"
      ? formatKpiCell({ status: "filled", isDerived: true, value: v }, { percent }).text
      : null;
  };
  const cur = current();

  async function save(pending: boolean): Promise<void> {
    let numericValue: number | null = null;
    if (!pending) {
      const n = normalizeCellInput(raw, {
        unit: p.indicator.unit,
        valueType: p.indicator.valueType,
      });
      if (n.input.status !== "filled" || typeof n.input.numericValue !== "number") {
        setErr(R.invalid);
        return;
      }
      numericValue = n.input.numericValue;
    }
    setBusy(true);
    setErr(null);
    const res = await saveReportedValue(p.indicator, p.entityId, p.year, numericValue);
    setBusy(false);
    if (res.ok) p.onSaved();
    else setErr(res.error);
  }

  return (
    <div
      data-testid="kpi-reported-field"
      className="mb-3 px-3 py-2 rounded-lg"
      style={{
        border: "1px dashed var(--color-border)",
        background: "color-mix(in srgb, var(--color-primary) 4%, white)",
      }}
    >
      <div className="flex items-baseline gap-2 text-xs" style={{ color: "var(--color-text-3)" }}>
        <span className="font-semibold" style={{ color: "var(--color-text-2)" }}>
          {R.fieldLabel}
        </span>
        {cur ? <span>{R.current(cur)}</span> : null}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <input
          aria-label={R.fieldLabel}
          style={{
            ...cellInputStyle,
            borderColor: err ? "var(--color-error)" : "var(--color-border)",
          }}
          value={raw}
          placeholder={R.placeholder}
          disabled={busy}
          onChange={(e) => {
            setRaw(e.target.value);
            setErr(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              void save(false);
            }
          }}
        />
        {p.indicator.unit ? (
          <span className="text-xs" style={{ color: "var(--color-text-3)", whiteSpace: "nowrap" }}>
            {percent ? "%" : p.indicator.unit}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => void save(false)}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            minHeight: 32,
          }}
        >
          {R.save}
        </button>
        <button
          type="button"
          onClick={() => void save(true)}
          disabled={busy}
          title={R.saveXxxTitle}
          data-testid="kpi-reported-xxx"
          className="px-3 py-1.5 rounded-lg text-xs font-semibold"
          style={{
            border: "1px solid var(--color-border)",
            color: "var(--color-text-2)",
            minHeight: 32,
            whiteSpace: "nowrap",
          }}
        >
          {R.saveXxx}
        </button>
      </div>
      <div className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
        {R.hint}
      </div>
      {err ? (
        <div role="alert" className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
          {err}
        </div>
      ) : null}
    </div>
  );
}
